import { Navigate, Route, Routes } from 'react-router-dom';
import { UserRole } from '@tutor/shared';
import { ProtectedRoute, PublicOnlyRoute } from './components/ProtectedRoute';
import { AppLayout } from './components/layout/AppLayout';

import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';

import { TutorDashboardPage } from './pages/tutor/TutorDashboardPage';
import { StudentsPage } from './pages/tutor/StudentsPage';
import { StudentDetailPage } from './pages/tutor/StudentDetailPage';
import { AssessmentsPage } from './pages/tutor/AssessmentsPage';

import { StudentDashboardPage } from './pages/student/StudentDashboardPage';
import { ProgressPage } from './pages/student/ProgressPage';

import { CalendarPage } from './pages/shared/CalendarPage';
import { LessonsPage } from './pages/shared/LessonsPage';
import { LessonDetailPage } from './pages/shared/LessonDetailPage';
import { HomeworkListPage } from './pages/shared/HomeworkListPage';
import { HomeworkDetailPage } from './pages/shared/HomeworkDetailPage';
import { ResourcesPage } from './pages/shared/ResourcesPage';
import { ProfilePage } from './pages/shared/ProfilePage';
import { ClassroomPage } from './pages/classroom/ClassroomPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { RootRedirect } from './pages/RootRedirect';

export function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route
        path="/login"
        element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicOnlyRoute>
            <RegisterPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicOnlyRoute>
            <ForgotPasswordPage />
          </PublicOnlyRoute>
        }
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* The classroom is full-bleed, so it sits outside the sidebar layout.
          Both roles reach the same component; capabilities come from the server. */}
      <Route
        path="/tutor/classroom/:lessonId"
        element={
          <ProtectedRoute role={UserRole.TUTOR}>
            <ClassroomPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/classroom/:lessonId"
        element={
          <ProtectedRoute role={UserRole.STUDENT}>
            <ClassroomPage />
          </ProtectedRoute>
        }
      />
      {/* Role-agnostic alias from the brief; redirects to the caller's own path. */}
      <Route path="/classroom/:lessonId" element={<ClassroomAliasRedirect />} />

      {/* Tutor portal */}
      <Route
        path="/tutor"
        element={
          <ProtectedRoute role={UserRole.TUTOR}>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/tutor/dashboard" replace />} />
        <Route path="dashboard" element={<TutorDashboardPage />} />
        <Route path="calendar" element={<CalendarPage perspective="tutor" />} />
        <Route path="students" element={<StudentsPage />} />
        <Route path="students/:studentId" element={<StudentDetailPage />} />
        <Route path="lessons" element={<LessonsPage perspective="tutor" />} />
        <Route path="lessons/:lessonId" element={<LessonDetailPage perspective="tutor" />} />
        <Route path="homework" element={<HomeworkListPage perspective="tutor" />} />
        <Route path="homework/:homeworkId" element={<HomeworkDetailPage perspective="tutor" />} />
        <Route path="assessments" element={<AssessmentsPage />} />
        <Route path="resources" element={<ResourcesPage perspective="tutor" />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* Student portal */}
      <Route
        path="/student"
        element={
          <ProtectedRoute role={UserRole.STUDENT}>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/student/dashboard" replace />} />
        <Route path="dashboard" element={<StudentDashboardPage />} />
        <Route path="calendar" element={<CalendarPage perspective="student" />} />
        <Route path="lessons" element={<LessonsPage perspective="student" />} />
        <Route path="lessons/:lessonId" element={<LessonDetailPage perspective="student" />} />
        <Route path="homework" element={<HomeworkListPage perspective="student" />} />
        <Route path="homework/:homeworkId" element={<HomeworkDetailPage perspective="student" />} />
        <Route path="progress" element={<ProgressPage />} />
        <Route path="resources" element={<ResourcesPage perspective="student" />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

import { useParams } from 'react-router-dom';
import { useAuthStore } from './stores/auth.store';

function ClassroomAliasRedirect() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const user = useAuthStore((state) => state.user);
  const status = useAuthStore((state) => state.status);

  if (status === 'idle' || status === 'restoring') return null;
  if (!user) return <Navigate to="/login" replace />;

  const base = user.role === UserRole.TUTOR ? '/tutor' : '/student';
  return <Navigate to={`${base}/classroom/${lessonId}`} replace />;
}
